"use client";
import { motion } from "framer-motion";
import Link from "next/link";
import { useMotion } from "@/contexts/MotionContext";
import Image from "next/image";
import { CARD_TEXTURE_SURFACE } from "@/lib/styles/surfaces";

interface ServiceCardProps {
  id: string;
  title: string;
  description: string;
  color: "teal" | "yellow" | "pink" | "peach" | "blue";
  icon: string; // Using our shape icons
  /** Override link (e.g. /prototype). Default: /services#{id} to match footer anchors. */
  href?: string;
}

const colors = {
  teal: "rgb(113, 217, 197)",
  yellow: "rgb(255, 226, 138)",
  pink: "rgb(246, 187, 205)",
  peach: "rgb(255, 142, 114)",
  blue: "rgb(70, 129, 209)",
};

export default function ServiceCard({
  id,
  title,
  description,
  color,
  icon,
  href,
}: ServiceCardProps) {
  const { isMotionEnabled } = useMotion();
  const to = href ?? `/services#${id}`;

  return (
    <Link href={to} className="h-full">
      <motion.div
        className={`group relative h-full overflow-hidden rounded-xl ${CARD_TEXTURE_SURFACE}`}
        whileHover={isMotionEnabled ? { scale: 1.02 } : {}}
        transition={{ duration: 0.2 }}
      >
        <div className="p-8 flex flex-col h-full">
          {/* Title with icon */}
          <div className="flex items-center gap-4 mb-4">
            <div className="flex h-10 w-10 items-center justify-center">
              <Image
                src={icon}
                alt=""
                width={200}
                height={200}
                className="w-full h-auto"
              />
            </div>
            <h3 className="text-2xl font-medium">{title}</h3>
          </div>

          {/* Content */}
          <div className="flex-1">
            <p className="text-text-muted-light dark:text-text-muted-dark">
              {description}
            </p>
          </div>

          {/* Color overlay on hover */}
          <div
            className="absolute inset-0 opacity-0 transition-opacity duration-300 group-hover:opacity-10"
            style={{ backgroundColor: colors[color] }}
          />
        </div>
      </motion.div>
    </Link>
  );
}
